'use client';
import { useMemo, useState } from 'react';
import { AdvancedMarker, Map } from '@vis.gl/react-google-maps';
import styles from './events.module.scss';
import { Button, LoadingSpinner, Pagination } from '@goodneighbor/ui';
import { Settings04 } from '@goodneighbor/ui/icons/untitled';
import useMediaQuery from '@/hooks/useMediaQuery';
import EventMarker from './components/EventMarker';
import EventPopup from './components/EventPopup';
import EventSlideOut from '@/components/EventSlideOut';
import { toCalendarDateValue } from '@/services/dateTime';
import { useEvents, DEFAULT_CENTER } from './hooks/useEvents';
import EventSearchBar from './components/EventSearchBar';
import EventFiltersDialog from './components/EventFiltersDialog';
import EventControls from './components/EventControls';
import EventList from './components/EventList';
import { cx } from 'class-variance-authority';

export default function Events() {
  const { breakpoint300: isMobileOrTablet } = useMediaQuery();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const {
    viewMode,
    setViewMode,
    searchValue,
    handleSearchChange,
    events,
    meta,
    isLoading,
    geolocationLoading,
    selectedEvent,
    selectedEventId,
    setSelectedEvent,
    location,
    setMarkerRef,
    mapPrimitive,
    popupRef,
    setIsManualDrag,
    centerPoint,
    handleMapDrag,
    handleMapBoundsChanged,
    page,
    setPage,
    resetToLocation,
    startDate,
    endDate,
    handleDateChange,
    clearFilters,
    causeFilters,
    toggleCauseFilter,
    availableCauseFilters,
    availableActivityTypeIds,
    activityTypes,
    activityTypeFilters,
    toggleActivityTypeFilter,
    skillTypeFilters,
    toggleSkillTypeFilter,
    availableSkillTypeIds,
    skillTypes,
    volunteersMin,
    volunteersMax,
    handleVolunteersNeededChange,
    locationValue,
    handleLocationChange,
    handleLocationSelect,
    handleLocationBlur,
    locationError,
    handleSearchSubmit,
    includeClosedEvents,
    toggleIncludeClosedEvents,
    activeFilterCount
  } = useEvents();

  const startDateValue = useMemo(
    () => toCalendarDateValue(startDate),
    [startDate]
  );
  const endDateValue = useMemo(() => toCalendarDateValue(endDate), [endDate]);

  if ((isLoading || geolocationLoading) && !selectedEventId) {
    return (
      <div className={styles.eventsLoadingState}>
        <LoadingSpinner />
      </div>
    );
  }

  const mapContent = (
    <>
      <Map
        className={styles.map}
        defaultCenter={
          location
            ? {
                lat: location.coords.latitude,
                lng: location.coords.longitude
              }
            : DEFAULT_CENTER
        }
        defaultZoom={10}
        maxZoom={18}
        id="EVENTS_MAP"
        mapId="EVENTS_MAP"
        onDrag={handleMapDrag}
        onBoundsChanged={handleMapBoundsChanged}
      >
        {events.map((event) => (
          <AdvancedMarker
            key={event.id}
            onMouseEnter={(enterEvent) => {
              if (!popupRef.current || !mapPrimitive) return;
              const markerEl =
                enterEvent.target as google.maps.marker.AdvancedMarkerElement;
              popupRef.current.innerHTML = event.title;
              popupRef.current.style.display = 'block';
              const { x, y } = markerEl.getBoundingClientRect();
              popupRef.current.style.left = `${x + window.scrollX + 20}px`;
              popupRef.current.style.top = `${y + window.scrollY - 30}px`;
            }}
            onMouseLeave={() => {
              if (!popupRef.current) return;
              popupRef.current.style.display = 'none';
            }}
            onClick={() => {
              if (popupRef.current) {
                popupRef.current.style.display = 'none';
              }
              setSelectedEvent({
                id: event.id
              });
            }}
            position={{
              lat: event?.coords?.coordinates[1] || 0,
              lng: event?.coords?.coordinates[0] || 0
            }}
            ref={(marker) => {
              setMarkerRef(marker, event.id);
            }}
            style={{
              position: 'relative'
            }}
          >
            <EventMarker />
          </AdvancedMarker>
        ))}
      </Map>
      <EventPopup popupRef={popupRef} />
    </>
  );

  return (
    <div className={styles.eventsPage}>
      {isMobileOrTablet && (
        <div className={styles.mapWrapper}>{mapContent}</div>
      )}

      <div className={styles.searchBarRow}>
        <div className={styles.searchContainer}>
          <p className={styles.mobileSearchLabel}>Search</p>
          <EventSearchBar
            searchValue={searchValue}
            onSearchChange={(value) => handleSearchChange(value)}
            locationValue={locationValue}
            onLocationChange={(value) => handleLocationChange(value)}
            onLocationSelect={handleLocationSelect}
            onLocationBlur={handleLocationBlur}
            locationError={locationError}
            onSubmit={handleSearchSubmit}
          />
          <div className={styles.mobileCta}>
            <Button
              color="secondary"
              iconLeading={<Settings04 />}
              onClick={() => setFiltersOpen(true)}
            >
              Filters
            </Button>
            <Button color="primary" onClick={handleSearchSubmit}>
              Search
            </Button>
          </div>
          <EventFiltersDialog
            startDateValue={startDateValue}
            endDateValue={endDateValue}
            handleDateChange={handleDateChange}
            clearFilters={clearFilters}
            selectedCauses={causeFilters}
            availableCauses={availableCauseFilters}
            availableActivityTypeIds={availableActivityTypeIds}
            activityTypes={activityTypes}
            activityTypeFilters={activityTypeFilters}
            availableSkillTypeIds={availableSkillTypeIds}
            skillTypes={skillTypes}
            skillTypeFilters={skillTypeFilters}
            onToggleCause={toggleCauseFilter}
            onToggleActivityType={toggleActivityTypeFilter}
            onToggleSkillType={toggleSkillTypeFilter}
            volunteersMinValue={volunteersMin}
            volunteersMaxValue={volunteersMax}
            onVolunteersNeededChange={handleVolunteersNeededChange}
            totalEvents={meta.pagination.total}
            includeClosedEvents={includeClosedEvents}
            onToggleIncludeClosedEvents={toggleIncludeClosedEvents}
            activeFilterCount={activeFilterCount}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
          />
        </div>
      </div>

      <div className={styles.content}>
        <div
          className={cx(
            styles.listSection,
            viewMode === 'list' && styles.listMode
          )}
        >
          <div className={styles.listWrapper}>
            <EventControls
              totalEvents={meta.pagination.total}
              viewMode={viewMode}
              onChangeViewMode={(mode) => setViewMode(mode)}
            />
            <div className={styles.eventListWrapper}>
              <EventList
                className={styles.eventList}
                events={events}
                viewMode={viewMode}
                centerPoint={centerPoint}
                resetToLocation={resetToLocation}
                onSelectEvent={(id) => setSelectedEvent({ id })}
                searchQuery={searchValue}
              />
            </div>
            <EventSlideOut
              isOpen={!!selectedEventId}
              onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                  setSelectedEvent(null);
                }
              }}
              event={selectedEvent}
              eventId={selectedEventId}
            />
          </div>
          <div className={styles.pagination}>
            <Pagination
              pageCount={meta.pagination.lastPage}
              pageIndex={page - 1}
              onPageChange={(newPage) => {
                setIsManualDrag(false);
                setPage(newPage + 1);
              }}
            />
          </div>
        </div>

        {!isMobileOrTablet && (
          <div className={styles.mapDesktop}>{mapContent}</div>
        )}
      </div>
    </div>
  );
}
